import { useState, useRef, useEffect } from "react";
import { allCountries } from "country-telephone-data";
import { verifyParticipantEmail } from "api/apiService";
import { useSnackBar } from "components/snackBar/SnackBarContext";
import { useAutoCopyInfo } from "context/AutoCopyInfoContext"; // ✅ import your new hook
import { reconstructFormDataOfParticipant } from "components/userForms/transformPayload";
import debounce from "lodash.debounce";
// Components
import Input from "../Input";
import Select from "../Select";
import Checkbox from "../Checkbox";
import fetchAddressSuggestions from "../../api/openstretadderss";
import {
  GENDER_OPTIONS,
  CONFERENCE_OPTIONS,
} from "components/userForms/constants";
// Assets
import Info from "../../assets/icons/ic_info.svg";
import Otp from "../../assets/icons/ic_otp.svg";

const PersonalDetails = ({
  formData,
  formErrors,
  handleChange,
  isParticipant,
  index,
  setAllParticipantsData, // pass this from parent
  autoFillOptions,
  setParticipantErrors,
  inputRefs,
}) => {
  const successSoundRef = useRef(null);
  const { showSnackbar } = useSnackBar();
  const { setParentAutoCopiedInfo } = useAutoCopyInfo();
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [isManualSelection, setIsManualSelection] = useState(false);
  const [tripName, setTripName] = useState("");
  const primaryEmail = localStorage.getItem("email");

  useEffect(() => {
    const trip = sessionStorage.getItem("tripName");
    setTripName(trip);
  }, []);

  useEffect(() => {
    const audio = new Audio("/confirmation-tone.mp3");
    successSoundRef.current = audio;
  }, []);

  const debouncedFetchSuggestions = useRef(
    debounce(async (value) => {
      if (value.trim().length < 3) {
        setSuggestions([]);
        setShowDropdown(false);
        return;
      }

      const results = await fetchAddressSuggestions(value, "");
      setSuggestions(results);
      setShowDropdown(true);
    }, 300),
  ).current;

  const handleAddressChange = (e) => {
    const value = e.target.value;
    handleChange(e);

    if (isManualSelection) {
      setIsManualSelection(false);
      return;
    }

    debouncedFetchSuggestions(value);
  };

  const handleSuggestionClick = (place) => {
    const {
      road,
      highway,
      village,
      neighbourhood,
      city,
      state_district,
      county,
      state,
      postcode,
      country,
    } = place.address;

    const addressParts = [road, highway, village, neighbourhood, county].filter(
      Boolean,
    );

    const fullAddress = addressParts.join(", ");

    setIsManualSelection(true);
    handleChange({ target: { name: "address", value: fullAddress } });
    handleChange({
      target: { name: "city", value: city ? city : state_district || "" },
    });
    handleChange({ target: { name: "state", value: state || "" } });
    handleChange({ target: { name: "zip", value: postcode || "" } });
    handleChange({ target: { name: "country", value: country || "" } });

    setSuggestions([]);
    setShowDropdown(false);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (index < inputRefs.length - 1) {
        inputRefs[index + 1].current.focus();
      }
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      if (
        isParticipant &&
        formData?.email &&
        // formData.email.includes(".com") // only trigger valid emails
        formData?.email &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) // ✅ validate all domains
      ) {
        try {
          const tripName = sessionStorage.getItem("tripName"); // or pass as prop
          const userData = await verifyParticipantEmail(
            formData.email,
            tripName,
          );

          if (userData.personalInfoDTO.personalId) {
            setParentAutoCopiedInfo(
              "As an existing registered user, your personal information will be seamlessly imported from your profile.",
            );
          } else {
            setParentAutoCopiedInfo("");
          }

          if (userData) {
            // Update participants state

            const updatedParticipant =
              reconstructFormDataOfParticipant(userData);
            setAllParticipantsData((prev) => {
              // if address auto-fill is ON, remove API’s address fields
              if (autoFillOptions?.address) {
                updatedParticipant.address = formData.address;
                updatedParticipant.city = formData.city;
                updatedParticipant.state = formData.state;
                updatedParticipant.zip = formData.zip;
                updatedParticipant.country = formData.country;
              }

              return [
                {
                  id: Date.now(),
                  formData: updatedParticipant,
                },
              ];
            });
          }
        } catch (error) {
          console.error("Failed to fetch user by email", error);
          const errorList = error?.response?.data;

          if (errorList.length > 0) {
            const { errorCode, message } = errorList[0];
            if (errorCode === "400") {
              if (message.startsWith("Email not found")) {
                console.log("error", message);
              } else {
                showSnackbar("error", message);
              }
            } else {
              showSnackbar("Something went wrong. Please try again.");
            }
          } else {
            showSnackbar("An unexpected error occurred.");
          }
        } finally {
          setIsModified(false);
        }
      }
    };

    if (isModified && !formData.personalId) fetchUser();
  }, [
    autoFillOptions,
    formData.email,
    formData.personalId,
    isModified,
    isParticipant,
    setAllParticipantsData,
    showSnackbar,
    setParentAutoCopiedInfo,
  ]);

  useEffect(() => {
    if (!showDropdown) return;

    const hideKeyboard = () => {
      const active = document.activeElement;
      if (active && active.tagName === "INPUT") active.blur();
    };

    window.addEventListener("touchstart", hideKeyboard, { passive: true });
    window.addEventListener("touchmove", hideKeyboard, { passive: true });
    window.addEventListener("scroll", hideKeyboard, { passive: true });

    return () => {
      window.removeEventListener("touchstart", hideKeyboard);
      window.removeEventListener("touchmove", hideKeyboard);
      window.removeEventListener("scroll", hideKeyboard);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (e) => {
      // Close dropdown when clicked outside the address input or dropdown
      if (
        !e.target.closest(".address-dropdown") && // dropdown
        !e.target.closest("input[name='address']") // address input
      ) {
        setShowDropdown(false);
        setSuggestions([]);
      }
    };

    const handleFocus = (e) => {
      // When any input except address gets focus → close dropdown
      if (e.target.name !== "address") {
        setShowDropdown(false);
        setSuggestions([]);
      }
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("focusin", handleFocus);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("focusin", handleFocus);
    };
  }, [showDropdown]);

  return (
    <>
      <div className="grid md:grid-cols-3 gap-4">
        <Input
          label="First Name"
          name="firstName"
          value={formData.firstName}
          onChange={(e) => {
            handleChange(e);
            localStorage.setItem("senderName", formData.firstName);
          }}
          error={formErrors.firstName}
          ref={inputRefs.firstName}
          onKeyDown={(e) => handleKeyDown(e, 0)}
          required
        />
        <Input
          label="Last Name"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          error={formErrors.lastName}
          ref={inputRefs.lastName}
          onKeyDown={(e) => handleKeyDown(e, 1)}
          required
        />
        <Input
          label="Preferred Name"
          name="preferredName"
          value={formData.preferredName}
          onChange={handleChange}
          error={formErrors.preferredName}
          ref={inputRefs.preferredName}
          required
        />

        <div className="md:col-span-3">
          <div
            name="gender"
            ref={inputRefs.gender}
            className="grid md:grid-cols-3 gap-4"
          >
            {/* Gender takes 1 col */}
            <div className="md:col-span-1">
              <Select
                label="Gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                error={formErrors.gender}
                options={GENDER_OPTIONS}
                onKeyDown={(e) => handleKeyDown(e, 2)}
                required
                className="col-span-1"
              />
            </div>
            {/* Email takes 2 cols */}
            <div className="md:col-span-2">
              <Input
                label="Email"
                name="email"
                type="email"
                icon={formData.email ? Otp : ""}
                disabled={formData.personalId || !isParticipant}
                value={formData.email}
                onChange={(e) => {
                  setIsModified(true);
                  handleChange(e);
                  // Prevent duplicate email (participant = primary user email)
                  if (isParticipant && e.target.value === primaryEmail) {
                    setParticipantErrors(
                      "email",
                      "This email is already used by the primary traveler",
                    );
                  }
                }}
                error={formErrors?.email}
                ref={inputRefs.email}
                required
              />
            </div>
          </div>
        </div>

        <div className="md:col-span-3">
          <div
            name="countryCode"
            ref={inputRefs.countryCode}
            className="grid md:grid-cols-2 gap-4"
          >
            <Select
              label="Country Code"
              name="countryCode"
              value={formData.countryCode}
              onChange={handleChange}
              placeholder="Select a country code"
              searchable={true} // ADD THIS LINE ONLY
              options={allCountries.map((country) => ({
                label: `+${country.dialCode} ${country.name}`,
                value: `+${country.dialCode} ${country.name}`,
              }))}
              required
              error={formErrors?.countryCode}
              ref={inputRefs.countryCode}
            />
            <Input
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={formErrors?.phone}
              ref={inputRefs.phone}
              onKeyDown={(e) => handleKeyDown(e, 3)}
              required
            />
          </div>
        </div>

        <div className="md:col-span-3 relative">
          <Input
            placeholder={"Search or add address"}
            label="Address"
            name="address"
            value={formData.address}
            onChange={(e) => {
              handleAddressChange(e);
              if (formData.addressConfirmation)
                handleChange({
                  target: {
                    name: "addressConfirmation",
                    value: false,
                  },
                });
            }}
            error={formErrors.address}
            ref={inputRefs.address}
            onKeyDown={(e) => handleKeyDown(e, 4)}
            required
          />

          {formData.address && showDropdown && suggestions.length > 0 && (
            <ul className="absolute z-40 bg-white border border-gray-300 w-full max-h-40 overflow-y-scroll rounded-md shadow-md">
              <li
                className="text-xs p-2 cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  setIsManualSelection(true);
                  handleChange({
                    target: { name: "address", value: formData.address },
                  });
                  setSuggestions([]);
                  setShowDropdown(false);
                }}
              >
                Add "{formData.address.trim()}"
              </li>
              {suggestions.map((sug, index) => (
                <li
                  key={index}
                  className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                  onClick={() => handleSuggestionClick(sug)}
                >
                  {sug.display_name}
                </li>
              ))}
            </ul>
          )}

          {formData.address && showDropdown && suggestions.length === 0 && (
            <ul className="absolute z-10 bg-white border border-gray-300 w-full max-h-40 overflow-y-auto rounded-md shadow-md">
              <li
                className="text-xs p-2 cursor-pointer hover:bg-gray-100"
                onClick={(e) => {
                  setIsManualSelection(true);
                  handleChange(e);
                  setSuggestions([]);
                  setShowDropdown(false);
                }}
              >
                Add "{formData.address.trim()}"
              </li>
            </ul>
          )}
        </div>

        <Input
          label="City"
          name="city"
          value={formData.city}
          onChange={(e) => {
            handleChange(e);
            if (formData.addressConfirmation)
              handleChange({
                target: {
                  name: "addressConfirmation",
                  value: false,
                },
              });
          }}
          error={formErrors.city}
          ref={inputRefs.city}
          onKeyDown={(e) => handleKeyDown(e, 5)}
          required
        />
        <Input
          label="State"
          name="state"
          value={formData.state}
          onChange={(e) => {
            handleChange(e);
            if (formData.addressConfirmation)
              handleChange({
                target: {
                  name: "addressConfirmation",
                  value: false,
                },
              });
          }}
          error={formErrors.state}
          ref={inputRefs.state}
          onKeyDown={(e) => handleKeyDown(e, 6)}
          required
        />
        <Input
          label="Zip"
          name="zip"
          value={formData.zip}
          onChange={(e) => {
            handleChange(e);
            if (formData.addressConfirmation)
              handleChange({
                target: {
                  name: "addressConfirmation",
                  value: false,
                },
              });
          }}
          error={formErrors.zip}
          ref={inputRefs.zip}
          onKeyDown={(e) => handleKeyDown(e, 7)}
          required
        />
        <Input
          label="Country"
          name="country"
          value={formData.country}
          onChange={(e) => {
            handleChange(e);
            if (formData.addressConfirmation)
              handleChange({
                target: {
                  name: "addressConfirmation",
                  value: false,
                },
              });
          }}
          error={formErrors.country}
          ref={inputRefs.country}
          onKeyDown={(e) => handleKeyDown(e, 8)}
          required
        />
        {tripName.includes("europ") && (
          <div className="md:col-span-2">
            <Select
              label="Conference / Mission / Union"
              name="conferenceMissionUnion"
              value={formData.conferenceMissionUnion}
              onChange={handleChange}
              error={formErrors.conferenceMissionUnion}
              options={CONFERENCE_OPTIONS}
              required
              className="col-span-1"
            />
          </div>
        )}
      </div>

      <div name="addressConfirmation" ref={inputRefs.addressConfirmation}>
        <Checkbox
          className="text-xs"
          name={`addressConfirmation${index !== undefined ? index : ""}`}
          checked={formData.addressConfirmation}
          onChange={(e) => {
            const isChecked = e.target.checked;

            handleChange({
              target: {
                name: "addressConfirmation",
                value: isChecked,
              },
            });

            if (isChecked && successSoundRef.current) {
              successSoundRef.current.currentTime = 0;
              successSoundRef.current.play();

              setTimeout(() => {
                successSoundRef.current.pause();
                successSoundRef.current.currentTime = 0;
              }, 2000);
            }
          }}
          label="I confirm that the address I entered is correct"
          required
        />
        {formErrors.addressConfirmation && (
          <div className="flex gap-1 w-fit text-[10px] bg-red-100 text-red-600 mt-1 rounded p-[3px]">
            <img src={Info} alt="info" />
            {formErrors.addressConfirmation}
          </div>
        )}
      </div>
    </>
  );
};

export default PersonalDetails;
